import type { PayType } from '../emp-payroll/emp-payroll.type';
import type { CommonParams } from '../../../../types/global.types';

// Employee Dashboard Params
export interface AllEmployeeParams extends CommonParams {
  business_id?: number;
  tenant_id?: string;
  department_id?: string;
  designation_id?: string;
  employee_type?: string;
  employee_types?: string[];
  status?: string;
  search?: string;
  payroll_year?: number;
  payroll_month?: string;
  /** JTI: only employees paid on this basis. Snake case on this endpoint. */
  pay_basis?: PayType;
}

// Employee Dashboard Item
export interface EmployeeData {
  id: string;
  user_id: string;
  photo: string | null;
  tenant_id: string;
  organization_id: string;
  name: string;
  role: string;
  manager_id: string | null;
  phone: string;
  email: string;
  house_address: string | null;
  is_face_recognition_enable: boolean;
  department_id: string;
  designation_id: string;
  employee_id: string;
  grade_id: string | null;
  salary: number;
  /**
   * JTI: how this employee is paid. Absent on every existing record, which is
   * why callers must treat `undefined` as MONTHLY.
   */
  /** JTI: the API answers in snake case and accepts `payBasis` on write. */
  pay_basis?: PayType;
  /** JTI: flat per-day rate, only set when `pay_basis === 'DAILY'`. */
  daily_rate_bdt?: number | null;
  /** JTI: values of the company's own extra fields, keyed by field key. */
  custom_fields?: Record<string, unknown>;
  joining_date: string; // ISO string
  emergency_contacts: any[]; // You can replace `any` with a proper type if needed
  date_of_birth: string | null;
  nid: string | null;
  blood_group: string | null;
  employee_type: string;
  educations: any[]; // Replace `any` with proper type if available
  skills: any[];
  certifications: any[];
  status: string | null;
  created_at: string;
  updated_at: string;
  photo_request_at: string;
  photo_request_status?: string;
  department: {
    id: string;
    name: string;
    organization_id: string;
    created_at: string;
    updated_at: string;
  };
  designation: {
    id: string;
    name: string;
    organization_id: string;
    created_at: string;
    updated_at: string;
  };
  manager: EmployeeData | null;
  // subordinates: EmployeeData[];
  organization: {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    logoUrl: string | null;
    createdAt: string; // ISO date string
    meta_hrx: {
      id: string;
      organization_id: string;
      operational_fiscal_year_start_month: string;
      operational_fiscal_year_end_month: string;
      is_face_recognition_enable: boolean;
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    meta: {
      id: string;
      admin_id: string;
      admin_name: string;
      admin_email: string;
      admin_phone: string;

      contact_person_id: string;
      contact_person_name: string;
      contact_person_email: string;
      contact_person_phone: string;

      description: string | null;
      company_size: string | null;
      country: string | null;
      industry_id: string | null;
      time_zone: string | null;

      is_active: boolean;
      created_at: string; // ISO date string
      updated_at: string; // ISO date string
      industry: {
        id: string;
        name: string;
      };
    };
  };
}

// Employee Dashboard List Response
export interface AllEmployeeResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    user_id: string;
    photo: string | null;
    employee_id: string;
    name: string;
    email: string;
    department: null | {
      id: string;
      name: string;
    };
    designation: {
      id: string;
      name: string;
    };
  }[];
  totalLength: number;
  currentLength: number;
  joining_date: string;
}

// Department Types
export interface DepartmentParams extends CommonParams {
  business_id?: number;
  status?: string;
  search?: string;
}

export interface DepartmentData {
  id: string;
  name: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  total_users: number;
  status: string;
}

export interface AllDepartmentResponse {
  data: DepartmentData[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// Designation Types
export interface DesignationParams extends CommonParams {
  business_id?: number;
  status?: string;
  search?: string;
}

export interface DesignationData {
  id: string;
  name: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  total_users: number;
  status: string;
}

export interface AllDesignationResponse {
  data: DesignationData[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface EmployeeStates {
  total_employees: number;
  present_employees: number;
  on_leave_employees: number;
  absent_employees: number;
  overtime_minutes: number;
}

export interface LastActionData {
  userId: string;
  name: string;
  department: string;
  designation: string;
  action: string;
  timestamp: string;
  message: string;
}

export interface AllFailedEmployeeData {
  success: boolean;
  message: string | null;
  data: {
    message: string;
    data: FailedEmployeeRow[];
    currentLength: number;
    totalLength: number;
  };
}

export interface FailedEmployeeRow {
  id: string;
  job_id: string;
  row_number: number;

  name: string;
  email: string;
  phone: string;

  employee_id: string | null;
  department_name: string;
  designation_name: string;

  joining_date: string;
  employee_type: string;

  manager_employee_id: string | null;
  salary: string;

  house_address: string | null;
  date_of_birth: string;
  nid: string;
  blood_group: string;

  failure_reason: string;
  error_details: string;

  attempted_at: string;
  uploaded_by: string | null;

  retry_count: number;
  is_resolved: boolean;

  resolved_at: string | null;
  resolved_by: string | null;

  created_at: string;
  updated_at: string;
}

export type FailedEmployeeListResponse = {
  message: string;
  data: {
    id: string;
    job_id: string;
    row_number: number;
    name: string;
    email: string;
    phone: string;
    employee_id: string;
    department_name: string;
    designation_name: string;
    joining_date: string;
    employee_type: string;
    manager_employee_id: string;
    salary: string;
    house_address: string;
    date_of_birth: string;
    nid: string;
    blood_group: string;
    failure_reason: string;
    error_details: string;
    attempted_at: string;
    uploaded_by: string;
    retry_count: number;
    is_resolved: boolean;
    resolved_at: string;
    resolved_by: string;
    created_at: string;
    updated_at: string;
  }[];
  currentLength: number;
  totalLength: number;
};

export type FailedEmployeeListParams = {
  page: number;
  limit: number;
  job_id?: string;
};

export interface PhotoPermissionDecideParams {
  userId: string;
  status: 'APPROVED' | 'REJECTED';
}

export interface UserSpecificLogParams extends CommonParams {
  organization_id?: string;
  user_id?: string;
  module_name?: string;
  action_type?: string;
  action_by?: string;
  entity_type?: string;
  entity_id?: string;
  startDate?: string;
  endDate?: string;
}

export interface UserSpecificLogData {
  id: string;
  module_name: string;
  action_type: string;
  action_by: string;
  user_id: string;
  entity_id: string;
  entity_type: string;
  updated_data: any;
  previous_data: any;
  log_message: string | null;
  log_message_admin: string | null;
  log_message_self: string | null;
  error_message: string | null;
  timestamp: string;
  created_at: string;
  organization_id: string;
  ip_address: string;
  user_agent: string;
  action_by_name: string;
}

export interface UserSpecificLogResponse {
  message: string;
  data: UserSpecificLogData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ------ Start: Bulk Employee Import V2 Types ------
export interface BulkImportV2Meta {
  total: number;
  page: number;
  last_page: number;
  limit: number;
}

export interface FailedImportJobV2 {
  jobId: string;
  fileName: string;
  failedCount: number;
  attemptedAt: string;
}

export interface FailedImportJobsV2Response {
  data: FailedImportJobV2[];
  meta: BulkImportV2Meta;
}

export interface FailedImportRecordV2 {
  id: string;
  job_id: string;
  row_number: number;
  organization_id: string;
  tenant_id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  employee_id: string | null;
  department_name: string | null;
  designation_name: string | null;
  joining_date: string | null;
  employee_type: string | null;
  manager_employee_id: string | null;
  salary: string | null;
  house_address: string | null;
  date_of_birth: string | null;
  nid: string | null;
  blood_group: string | null;
  gender: string | null;
  tin: string | null;
  office_name: string | null;
  overtime_policy_name: string | null;
  grade_name: string | null;
  bank_name: string | null;
  account_name: string | null;
  branch: string | null;
  account_number: string | null;
  routing_number: string | null;
  failure_reason: string;
  error_details: string;
  file_name: string;
  attempted_at: string;
  uploaded_by: string | null;
  retry_count: number;
  is_resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface FailedImportRecordsV2Response {
  data: FailedImportRecordV2[];
  meta: BulkImportV2Meta;
}

export interface FailedImportRecordsV2Params {
  page: number;
  limit: number;
  job_id?: string;
}
// ------ End: Bulk Employee Import V2 Types ------


// ---------------------------------------------------------------------------
// JTI: employee attachments
// ---------------------------------------------------------------------------

/** Suggested categories. The API stores whatever string it is given. */
export const DOCUMENT_TYPES = [
  { label: 'NID', value: 'NID' },
  { label: 'Passport', value: 'PASSPORT' },
  { label: 'CV', value: 'CV' },
  { label: 'Certificate', value: 'CERTIFICATE' },
  { label: 'Contract', value: 'CONTRACT' },
  { label: 'Offer Letter', value: 'OFFER_LETTER' },
  { label: 'Other', value: 'OTHER' },
] as const;

export type DocumentType = (typeof DOCUMENT_TYPES)[number]['value'];

export interface EmployeeAttachment {
  id: string;
  employeeId: string;
  documentName: string;
  documentType: string;
  fileName: string;
  fileUrl: string;
  fileType?: string;
  fileSize?: number;
  /** An id right after upload, the full user once the list is re-read. */
  uploadedBy?:
    | string
    | { id: string; name: string; employee_id: string | null }
    | null;
  createdAt?: string;
  updatedAt?: string;
}

/** The list endpoint answers with a flat array, not a paginated envelope. */
export interface EmployeeAttachmentListResponse {
  success: boolean;
  message: string | null;
  data: EmployeeAttachment[];
}

export interface UploadEmployeeAttachmentsBody {
  employeeId: string;
  documentName: string;
  documentType: string;
  files: File[];
}
